import { useState, useMemo } from "react";
import {
  getFuelTypes,
  getTransmissionTypes,
  getFilteredVariants,
  generateVariantKey,
  createUniqueVariantKey
} from "@helpers/variantHelpers";
import { VariantDetail, MileageVariants } from "@interfaces/mileage";

export const useMileageVariantSectionData = (initialMileageVariants: MileageVariants) => {
  const [selectedFuelType, setSelectedFuelType] = useState<string | null>(null);
  const [selectedTransmission, setSelectedTransmission] = useState<string | null>(null);
  
  const [variantDistances, setVariantDistances] = useState<{ [key: string]: number }>({});
  
  const variantKeyMap = useMemo(() => {
    const keyMap = new Map<VariantDetail, string>();
    const usedKeys = new Set<string>();
    
    initialMileageVariants.variants.forEach(variant => {
      const uniqueKey = createUniqueVariantKey(variant, usedKeys);
      keyMap.set(variant, uniqueKey);
    });
    
    return keyMap;
  }, [initialMileageVariants.variants]);

  const fuelTypes = useMemo( () => getFuelTypes(initialMileageVariants.variants),[initialMileageVariants.variants]);

  const transmissionTypes = useMemo(
    () => getTransmissionTypes(initialMileageVariants.variants),
    [initialMileageVariants.variants]
  );

  const filteredVariants = useMemo(
    () => getFilteredVariants(initialMileageVariants.variants, selectedFuelType, selectedTransmission),
    [initialMileageVariants.variants, selectedFuelType, selectedTransmission]
  );

  const updateVariantDistance = (variant: VariantDetail, distance: number) => {
    const key = variantKeyMap.get(variant) || generateVariantKey(variant);
    setVariantDistances(prev => ({
      ...prev,
      [key]: distance
    }));
  };

  const getVariantDistance = (variant: VariantDetail) => {
    const key = variantKeyMap.get(variant) || generateVariantKey(variant);
    return variantDistances[key] ?? 40;
  };
  
  const getVariantKey = (variant: VariantDetail) => {
    return variantKeyMap.get(variant) || generateVariantKey(variant);
  };

  return {
    selectedFuelType,
    setSelectedFuelType,
    selectedTransmission,
    setSelectedTransmission,
    fuelTypes,
    transmissionTypes,
    filteredVariants,
    heading: initialMileageVariants.heading,
    subHeading: initialMileageVariants.subHeading,
    variantDistances,
    updateVariantDistance,
    getVariantDistance,
    generateVariantKey: getVariantKey,
  };
}; 